import { useState, useMemo, useCallback, useEffect, useRef, useContext } from 'react';
import { Tab, Button, Icon } from 'semantic-ui-react';
import PassFailResult from '../../../components/PassFailResult';
import DetailsSegment from '../../../components/DetailsSegment';
import CenterCell from '../../../components/table/CenterCell';
import { getAPI, postAPI } from '../../../libs/fetchAPI';
import Duration from '../../../components/Duration';
import SingleDetailsModal from '../../../components/SingleDetailsModal';
import ErrorLog from '../../../components/TestErrors';
import ServerPaginatedTable from '../../../components/table/ServerPaginatedTable';
import AppContext from '../../../context/HeaderContext';
import RightCell from '../../../components/table/RightCell';
import IntroMessage from '../../../components/IntroMessage';
import JiraLink from '../../../components/JiraLink';

function Suite() {
	const { current_project_id, user }                = useContext(AppContext);
	const [ loading, setLoading ]                     = useState(false);
	const [ loading_disable, setLoadingDisable ]      = useState(false);
	const [ loading_details, setLoadingDetails ]      = useState({});
	const [ loading_error_log, setLoadingErrorLog ]   = useState(false);
	const [ suite_info, setSuiteInfo ]                = useState([]);
	const [ suites, setSuites ]                       = useState([]);
	const [ error_log, setErrorLog ]                  = useState([]);
	const [ tests, setTests ]                         = useState([]);
	const [ test_errors, setTestErrors ]              = useState([]);
	const [ show_details_modal, setShowDetailsModal ] = useState(false);
	const [ page_count, setPageCount ]                = useState();
	const [ is_disabled, setIsDisabled ]              = useState(false);
	const fetch_id_ref                                = useRef(0);

	/* eslint-disable */
	useEffect(() => {
		setLoadingErrorLog(true);

		const param_query = new URLSearchParams(window.location.search);

		const params = {
			spec_file  : param_query.get(`spec_file`),
			version    : param_query.get(`version`),
			project_id : current_project_id,
		};

		const error_params = { ...params, ...{
			sort_key  : `run_date`,
			limit     : 100,
		}};

		Promise.all([
			getAPI(`/suite_info/errors?${new URLSearchParams(error_params)}`),
			getAPI(`/general_suite_info?${new URLSearchParams(params)}`),
		]).then((response) => {
				setErrorLog(response[0].data);

				const info = response[1].data[0]

				const tmp_suite_info = [
					{
						header  : `Run Command`,
						title   : <a href="#">Copy</a>,
						handler : handleCopyCommand,
						params  : { spec_file : info.spec_file, site : user.email.split(`@`)[0] }
					},
					{
						header : `Spec File`,
						title  : info.spec_file,
					},
					{
						header : `Suite Title`,
						title  : info.title,
					},
					{
						header : `Version`,
						title  : info.version || `N/A`
					},
					{
						header : `Retry %`,
						title  : `N/A`
					},
					{
						header : `Pass %`,
						title  : `N/A`
					},
				];

				if(user.admin) {
					tmp_suite_info.push({
						header  : `Disable Test`,
						title   : is_disabled || info.disabled ? <Icon name="cancel" /> : (!loading_disable ? <a href="#">Disable</a> : <Icon name="spinner" />),
						handler : handleDisableTest,
						params  : { spec_file : info.spec_file },
					});
				}

				setSuiteInfo(tmp_suite_info);
				setLoadingErrorLog(false);
			});
	}, [is_disabled]);
	/* eslint-enable */

	function handleCopyCommand({ spec_file, site }) {
		const text = `npm run headless -- --spec ${spec_file} --url https://e2e.focus.support/${site}`;

		navigator.clipboard.writeText(text);
	}

	async function handleDisableTest({ spec_file }) {
		setLoadingDisable(true);

		await postAPI(`/suite_info/disable?${new URLSearchParams({ spec_file })}`);

		setLoadingDisable(false);
		setIsDisabled(true);
	}

	function handleCloseDetails() {
		setShowDetailsModal(false);
	}

	function handleViewDetails(id) {
		setLoadingDetails({ [id] : true });

		Promise.all([
			getAPI(`/suites/${id}/tests`),
			getAPI(`/suites/${id}/errors`)
		]).then((response) => {
				const [ tmp_tests, tmp_errors ] = response;

				setTests(tmp_tests.data.reverse());
				setTestErrors(tmp_errors.data);
				setShowDetailsModal(true);
				setLoadingDetails({});
			});
	}

	/* eslint-disable */
	const fetchTableData = useCallback(({ pageIndex, pageSize, filters, sortBy }) => {
		if(fetch_id_ref.current > 1) {
			return;
		}

		let sort_key       = `run_date`;
		let sort_direction = `DESC`;

		if(sortBy.length) {
			sort_key       = sortBy[0].id;
			sort_direction = sortBy[0].desc ? `DESC` : `ASC`;
		}

		const params = {
			project_id     : current_project_id,
			limit          : pageSize,
			offset         : pageSize * pageIndex,
			filters        : JSON.stringify(filters),
			spec_file      : new URLSearchParams(window.location.search).get(`spec_file`),
			version        : new URLSearchParams(window.location.search).get(`version`),
			sort_key,
			sort_direction,
		};

		setLoading(true);
		getAPI(`/suite_info_single?${new URLSearchParams(params)}`)
			.then(({ data, pages }) => {
				setSuites(data);
				setPageCount(pages);
				setLoading(false);
			});
	}, []);
	/* eslint-enable */

	const columns = useMemo(() => [
		{
			Header   : `Run Date`,
			accessor : `run_date`,
			Cell     : ({ cell }) => <span>{new Date(`${cell.value} UTC`).toLocaleString()}</span>
		},
		{
			Header   : `Run Name`,
			accessor : `run_name`,
			Cell     : ({ cell }) => <JiraLink issue_key={cell.value} />
		},
		{
			Header   : `Capabilities`,
			accessor : `capabilities`,
		},
		{
			Header   : `Retries`,
			accessor : `retries`,
			Cell     : ({ cell }) => <RightCell>{cell.value}</RightCell>
		},
		{
			Header   : `Duration`,
			accessor : `duration`,
			Cell     : ({ cell }) => <RightCell><Duration duration={cell.value} /></RightCell>
		},
		{
			Header   : `Result`,
			accessor : `passed`,
			Cell     : ({ cell }) => <PassFailResult passed={cell.value} skipped={cell.row.values.skipped} />
		},
		{
			Header   : `Skipped Hidden`,
			accessor : `skipped`,
		},
		{
			Header   : ``,
			accessor : `id`,
			collapse : true,
			Cell     : ({ cell }) => <CenterCell><Button loading={loading_details[cell.value]} color="teal" onClick={() => handleViewDetails(cell.value)}>Details</Button></CenterCell>
		}
	], [loading_details]);

	/*
	if(!id || id === `none`) {
		return <Message>No suite selected</Message>
	}
	*/

	const panes = [
		{
			menuItem : `History`,
			render   : () => (
				<Tab.Pane attached={false}>
					<ServerPaginatedTable
						columns={columns}
						data={suites}
						fetchTableData={fetchTableData}
						loading={loading}
						pageCount={page_count}
						default_page_size="10"
						styles={{ position : `relative` }}
						hidden_columns={[`skipped`]}
					/>
				</Tab.Pane>
			)
		},
		{
			menuItem : `Error Log`,
			render   : () => (
				<Tab.Pane attached={false}>
					{loading_error_log ? (
						<div>Loading...</div>
					) : (
						<ErrorLog
							errors={error_log}
							show_date={true}
						/>
					)}
				</Tab.Pane>
			)
		},
		/*
		{
			menuItem : `Charts`,
			render   : () => (
				<Tab.Pane attached={false}>
					<Charts />
				</Tab.Pane>
			)
		}
		*/
	];

	return (
		<>
			<SingleDetailsModal
				open={show_details_modal}
				handleClose={handleCloseDetails}
				tests={tests}
				errors={test_errors}
			/>

			<IntroMessage
				id={4}
				message='The Spec History -> Selected Suite page lists the history of an individual spec for each test run.'
			/>

			<DetailsSegment
				details={suite_info}
			/>

			<Tab menu={{ secondary : true, pointing : true }} panes={panes} />
		</>
	);
}

export default Suite;
